/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable react/no-danger */
import React, { useState, useEffect, useRef } from 'react';
import { connect, history } from 'umi';

import { Toast, PasscodeInput, NumberKeyboard } from 'antd-mobile';
import { Modal } from '@digitalc/dxp-ui';
import moment from 'moment';
import copy from 'copy-to-clipboard';
import DOMPurify from 'dompurify';
import useGCPI18n from '@/hooks/useGCPI18n';
import { sanitizeParam } from '@/utils/sanitize';
import { goBack, formatDateString, onHistoryPush } from '@/utils/utils';
import Header from '@/components/Header';
import { Clickable } from '@/components';
import BaseSpin from '@/components/BaseSpin';
import hiappLoading from '@/assets/images/hiapp_loading.gif';
import lockIcon from '@/assets/images/memberShipCenter/myRewards/lock.png';
import copyImg from '@/assets/images/memberShipCenter/myRewards/copy.png';
import fail from '@/assets/images/memberShipCenter/myRewards/fail.png';
// import noDataImg from '@/assets/images/memberShipCenter/myRewards/noData.png';
import styles from './index.less';

// 包装 NumberKeyboard：强制常驻 + 挂载到弹窗内部容器
const AlwaysVisibleKeyboard = ({ visible, onInput, onDelete, onClose, ...rest }) => (
  <NumberKeyboard
    {...rest}
    visible
    onInput={onInput}
    onDelete={onDelete}
    getContainer={() => document.getElementById('pin-keyboard-mount') || document.body}
  />
);

// /* 内联数字键盘备选方案（如果 NumberKeyboard 效果不好，取消注释使用）
// const InlineKeyboard = ({ onInput, onDelete }) => {
//   const keys = [1, 2, 3, 4, 5, 6, 7, 8, 9];
//   return (
//     <div className={styles.pinKeypad}>
//       {keys.map(k => (
//         <div key={k} className={styles.key} onClick={() => onInput?.(String(k))}>{k}</div>
//       ))}
//       <div className={styles.keyEmpty} />
//       <div className={styles.key} onClick={() => onInput?.('0')}>0</div>
//       <div className={styles.key} onClick={() => onDelete?.()}>
//         <span className={styles.deleteIcon}>⌫</span>
//       </div>
//     </div>
//   );
// };
// */

const MemberMyRewardsDetail = props => {
  const { dispatch, rewardDetail = {}, loading, pinLength = 6 } = props;
  const GCPI18n = useGCPI18n();
  const [modalType, setModalType] = useState(null); // 'pin' | 'coupon' | null
  const [pinCode, setPinCode] = useState('');
  const [showFailModal, setShowFailModal] = useState(false);
  const passcodeRef = useRef(null);
  const [keyboardReady, setKeyboardReady] = useState(false);

  // 弹窗打开时：先确保 #pin-keyboard-mount 渲染到 DOM，再让键盘挂载
  useEffect(() => {
    if (modalType === 'pin') {
      setKeyboardReady(false);
      requestAnimationFrame(() => {
        setKeyboardReady(true);
        setTimeout(() => passcodeRef.current?.focus(), 50);
      });
    } else {
      setKeyboardReady(false);
    }
  }, [modalType]);

  const { query: rawQuery } = history.location;
  const rewardInstanceId = sanitizeParam((rawQuery || {}).rewardInstanceId);
  const benefitRewardId = sanitizeParam((rawQuery || {}).benefitRewardId);
  const notNewPage = sanitizeParam((rawQuery || {}).notNewPage);
  const nextLevelName = rewardDetail?.levelName || '';
  const isLocked = !rewardInstanceId && benefitRewardId;

  const getPhoneNumber = () => {
    try {
      const initApp = JSON.parse(window.localStorage.getItem('initApp') || '{}');
      return initApp.phone || initApp.mobile || '';
    } catch {
      return '';
    }
  };

  const { couponSn, couponBrand, requirePinForClaim = false } = rewardDetail.coupon || {};

  useEffect(() => {
    dispatch({ type: 'memberShipCenter/queryPinFormat' });
    if (isLocked) {
      dispatch({
        type: 'memberShipCenter/queryBenefitRewardDetail',
        payload: { benefitRewardId },
      });
    } else if (rewardInstanceId) {
      dispatch({
        type: 'memberShipCenter/queryRewardDetail',
        payload: { rewardInstanceId },
      });
    }
    return () => {
      dispatch({
        type: 'memberShipCenter/save',
        payload: { rewardDetail: {} },
      });
    };
  }, []);

  const getRewardState = () => {
    if (isLocked) return 'locked';
    const { state, useState: useSt, expDate, remainingQuantity } = rewardDetail;
    if (state === 'E') return 'expired';
    if (state === 'A' && remainingQuantity === 0) return 'outOfStock';
    if (useSt === 'B') return 'used';
    if (state === 'A') return 'pendingClaim';
    if (state === 'B' && useSt === 'A') {
      if (expDate && moment().isSameOrAfter(moment(formatDateString(expDate)))) {
        return 'claimedExpired';
      }
      return 'claimedUnused';
    }
    return 'unknown';
  };

  const rewardState = getRewardState();
  const isGreyedOut = ['expired', 'outOfStock', 'claimedExpired'].includes(rewardState);
  const isOutOfStock = rewardState === 'outOfStock';
  const showUseButton = ['pendingClaim', 'claimedUnused'].includes(rewardState);

  const handleGoBack = () => {
    if (notNewPage) {
      history.goBack();
      return;
    }
    goBack();
  };

  const doClaimOrUse = (pin = '') => {
    const actionType =
      rewardState === 'pendingClaim'
        ? 'memberShipCenter/claimRewardInstance'
        : 'memberShipCenter/useRewardInstance';
    const payload =
      rewardState === 'pendingClaim'
        ? { rewardInstanceId, needToUse: requirePinForClaim, pinCode: pin }
        : { rewardInstanceId, pinCode: pin };

    dispatch({ type: actionType, payload }).then(res => {
      if (res?.resultCode === '0') {
        setModalType(null);
        setPinCode('');
        history.push('/memberSuccessPage');
      } else {
        setShowFailModal(true);
        setPinCode('');
        setModalType(null);
      }
    });
  };

  const handleUseNow = () => {
    const { rewardType } = rewardDetail;
    if (String(rewardType) === '22' && couponSn) {
      setModalType('coupon');
    } else if (String(rewardType) === '22' && requirePinForClaim) {
      setPinCode('');
      setModalType('pin');
    } else {
      doClaimOrUse('');
    }
  };

  const handleCopyCoupon = () => {
    if (couponSn) {
      copy(couponSn);
      Toast.show({ icon: 'success', content: GCPI18n({ id: 'tip_membership_copied' }) });
    }
  };
  const handleGoUnlockGuide = () => {
    onHistoryPush('/memberShipLevelGuide');
  };
  const phoneNumber = getPhoneNumber();

  /**
   * Render bottom action bar
   * @param {Object} params
   * @param {React.ReactNode} params.content - Content to display
   * @param {Function} [params.onClick] - Click handler (optional, if not provided renders as div)
   */
  const renderBottomBar = ({ content, onClick }) => (
    <div className={styles.bottomBar}>
      {onClick ? (
        <Clickable className={styles.bottomItem} onClick={onClick}>
          {content}
        </Clickable>
      ) : (
        <div className={styles.bottomItem}>{content}</div>
      )}
    </div>
  );

  const renderStatusText = () => {
    if (rewardState === 'claimedExpired') return GCPI18n({ id: 'lb_membership_expired' });
    return null;
  };

  // 手搓弹窗（已弃用，改用 DXP Modal 组件）
  // const renderFailModal = () => (
  //   <div className={styles.modalOverlay} onClick={() => setShowFailModal(false)}>
  //     <div className={styles.failModal} onClick={e => e.stopPropagation()}>
  //       <div className={styles.failClose} onClick={() => setShowFailModal(false)}>✕</div>
  //       <img className={styles.failImage} src={fail} alt="" />
  //       <div className={styles.failTitle}>That didn&apos;t work</div>
  //       <div className={styles.failSubtitle}>{failMsg}</div>
  //       <div className={styles.failBtn} onClick={() => setShowFailModal(false)}>OK, got it</div>
  //     </div>
  //   </div>
  // );

  const renderRewardModal = () => (
    <Clickable className={styles.modalOverlay} onClick={() => setModalType(null)}>
      <Clickable className={styles.couponModal} onClick={e => e.stopPropagation()}>
        <div className={styles.drawerBar} />

        <div className={styles.couponHeader}>
          {couponBrand && <div className={styles.couponBrand}>{couponBrand}</div>}
          <div className={styles.couponTitle}>{rewardDetail.rewardName}</div>
        </div>

        <div className={styles.dividerBar} />

        <div className={styles.couponInfo}>
          <div className={styles.couponSectionTitle}>
            {GCPI18n({ id: 'lb_membership_redemption_details' })}
          </div>
          <div className={styles.couponRow}>
            <span>{GCPI18n({ id: 'lb_membership_quantity' })}</span>
            <span>1</span>
          </div>
          <div className={styles.couponRow}>
            <span>{GCPI18n({ id: 'lb_membership_for_mobile_number' })}</span>
            <span>{phoneNumber}</span>
          </div>
        </div>

        <div className={styles.dividerBar} />

        {modalType === 'pin' ? (
          <div className={styles.pinEntryWrap}>
            <div className={styles.pinInstruction}>
              {GCPI18n({ id: 'tip_membership_ask_staff_pin' })}
            </div>
            {keyboardReady && (
              <PasscodeInput
                ref={passcodeRef}
                length={pinLength}
                plainText
                seperated
                value={pinCode}
                onChange={setPinCode}
                onFill={val => {
                  doClaimOrUse(val);
                }}
                keyboard={<AlwaysVisibleKeyboard />}
              />
            )}
            <div id="pin-keyboard-mount" />
          </div>
        ) : (
          <div className={styles.couponSnWrap}>
            <div className={styles.couponInstruction}>
              {GCPI18n({ id: 'tip_membership_visit_website_copy' })}
            </div>
            <div className={styles.couponCodeBox}>
              <span className={styles.couponCodeText}>{couponSn}</span>
              <Clickable as="span" className={styles.copyIcon} onClick={handleCopyCoupon}>
                <img src={copyImg} alt="Copy" />
              </Clickable>
            </div>
          </div>
        )}
      </Clickable>
    </Clickable>
  );

  return (
    <div className={styles.pageContainer}>
      <Header
        title={GCPI18n({ id: 'lb_membership_reward_details' })}
        hideClose
        isArrowLeft
        isHiddenBorder
        goBack={handleGoBack}
        titleFontFamily="Lato"
      />
      <BaseSpin spinning={loading} loadingImg={hiappLoading}>
        <div className={styles.contentContainer}>
          <div className={`${styles.heroImage} ${isGreyedOut ? styles.greyedOut : ''}`}>
            <img src={rewardDetail.winningImageUrl} alt="" />
            {isLocked && (
              <div className={styles.lockWrapper}>
                {GCPI18n({ id: 'lb_membership_unlock_rewards' }, { levelName: nextLevelName })}
              </div>
            )}
            {renderStatusText() && <div className={styles.statusOverlay}>{renderStatusText()}</div>}
          </div>

          <div className={styles.rewardInfo}>
            {couponBrand && <div className={styles.brandName}>{couponBrand}</div>}
            <div className={styles.rewardName}>
              {isLocked && <img src={lockIcon} alt="" className={styles.lockIconSmall} />}
              {rewardDetail.rewardName}
            </div>
            {isOutOfStock && (
              <div className={styles.outOfStockBadge}>
                {GCPI18n({ id: 'lb_membership_fully_redeemed' })}
              </div>
            )}
            {/* <div className={styles.rewardInfoMsg}>
              {GCPI18n({ id: 'lb_membership_rewards_description' })}
            </div> */}
          </div>

          <div className={styles.spacing} />

          {rewardDetail.comments && (
            <div
              className={styles.comments}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rewardDetail.comments) }}
            />
          )}
        </div>

        {showUseButton &&
          renderBottomBar({
            content: GCPI18n({ id: 'btn_membership_use_now' }),
            onClick: handleUseNow,
          })}

        {isLocked &&
          renderBottomBar({
            content: GCPI18n({ id: 'lb_membership_how_to_unlock' }, { levelName: nextLevelName }),
            onClick: handleGoUnlockGuide,
          })}
      </BaseSpin>

      {modalType && renderRewardModal()}
      {showFailModal && (
        <Modal
          breakpoint="mobile"
          size="50%"
          open={showFailModal}
          onCancel={() => setShowFailModal(false)}
          className={styles.failModalWrap}
          footer={
            <Clickable onClick={() => setShowFailModal(false)} className={styles.failModalBtn}>
              {GCPI18n({ id: 'btn_membership_ok_got_it' })}
            </Clickable>
          }
        >
          <div className={styles.failContentWrap}>
            <div className={styles.failErrImg}>
              <img src={fail} alt="" />
            </div>
            <div className={styles.failTitle}>
              {GCPI18n({ id: 'lb_membership_that_didnt_work' })}
            </div>
            <div className={styles.failSubtitle}>
              {GCPI18n({ id: 'tip_membership_something_wrong' })}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default connect(({ memberShipCenter, index, loading }) => ({
  rewardDetail: memberShipCenter.rewardDetail,
  pinLength: memberShipCenter.pinLength,
  dxpToken: index.dxpToken,
  loading:
    loading.effects['memberShipCenter/queryRewardDetail'] ||
    loading.effects['memberShipCenter/queryBenefitRewardDetail'] ||
    loading.effects['memberShipCenter/claimRewardInstance'] ||
    loading.effects['memberShipCenter/useRewardInstance'] ||
    false,
}))(MemberMyRewardsDetail);
